import axios from "axios";
import dayjs from "dayjs";
import { message } from "antd";
import useAuthStore from "../../stores/useAuthStore";
import useRestaurantStore from "../../stores/useRestaurantStore";
import { notifyTemplateNotConfigured, PRINT_TEMPLATES } from "./printTemplateErrors";

/**
 * Mobile has no local hardware-controller HTTP server to POST to (desktop's
 * embedded print service only runs on the desktop, at 127.0.0.1 - on a phone
 * that's the phone itself, so the request below always fails there). Convert
 * the admin-authored KOT XML into the JSON element template the mobile bridge's
 * printTemplate understands, generically via DOMParser rather than assuming a
 * fixed column layout, since the <line>/<text> shape comes from the site's own
 * kotPrintTemplate and isn't fixed.
 */
// Nothing parsed this XML client-side before kotXmlToTemplate — the desktop
// hardwareController service consumed it directly, however lenient that is.
// DOMParser is not lenient: unescaped `&`/`<`/`>` in interpolated item data
// (weight, notes) breaks the whole document into a single <parsererror>,
// which reads back as zero <line> elements - a silently blank ticket rather
// than a visible failure. Escape everything interpolated into a <text> body.
const escapeXml = (value) =>
  String(value ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));

const kotXmlToTemplate = (xml) => {
  const doc = new DOMParser().parseFromString(xml, "application/xml");
  const parseError = doc.getElementsByTagName("parsererror")[0];
  if (parseError) {
    console.error("[kotPrinter] KOT XML failed to parse — printing an empty ticket:", parseError.textContent, xml);
    return { elements: [] };
  }
  const elements = [];
  for (const line of Array.from(doc.getElementsByTagName("line"))) {
    const texts = Array.from(line.getElementsByTagName("text"));
    let startPosition = 0;
    texts.forEach((text, idx) => {
      const length = parseInt(text.getAttribute("length"), 10) || undefined;
      elements.push({
        type: "text",
        content: text.textContent || "",
        alignment: text.getAttribute("align") || "left",
        startPosition,
        length,
        addNewline: idx === texts.length - 1,
      });
      if (length) startPosition += length;
    });
  }
  console.debug("[kotPrinter] parsed KOT XML into", elements.length, "text elements from", doc.getElementsByTagName("line").length, "lines. Source XML:", xml);
  return { elements };
};

export const kotPrinter = (printReciptData, fbOrderId) => {
  const tillData = useAuthStore.getState().tillData;
  const hasKotItems = Object.values(printReciptData || {}).some((items) => Array.isArray(items) && items.length > 0);
  if (hasKotItems && !tillData?.tillAccess?.cwrTill?.kotPrintTemplate?.xmlcode2) {
    notifyTemplateNotConfigured(PRINT_TEMPLATES.kot, "KOT");
    return;
  }
  const printerURL = tillData.tillAccess.cwrTill.hardwareController.imageUrl;
  let selectedTableData = useRestaurantStore.getState().tableName;

  let printData = Object.entries(printReciptData).map((element, index) => {
    if (element[1].length > 0) {
      let newData = element[1].reduce((result, current) => {
        const category = current.productCategoryName;
        if (!result[category]) {
          result[category] = [];
        }
        if (current.order === "N") {
          result[category].push(current);
        }
        return result;
      }, {});
      if (Object.keys(newData).length === 0) {
        delete element.newData;
      } else {
        element.newData = newData;
      }
    } else {
      delete element.newData;
    }
    return element;
  });

  let data = "";
  // Per-printer groups, captured alongside `data` so mobile can fire one
  // printTemplate() call per kitchen station (see isMobile branch below) —
  // `item[0]` is the printReciptData key, which IS the station's printerId.
  const mobileGroups = [];
  printData.map((item) => {
    for (const key in item.newData) {
      if (Array.isArray(item.newData[key]) && item.newData[key].length === 0) {
        delete item.newData[key];
      }
    }
    if (Object.keys(item.newData).length !== 0) {
      let printDataXml = tillData.tillAccess.cwrTill.kotPrintTemplate?.xmlcode2;
      printDataXml = printDataXml?.replace("<output>", "");
      printDataXml = printDataXml?.replace("</output>", "");
      printDataXml = printDataXml?.replace("@@Printer@@", `${item[0]}`);
      printDataXml = printDataXml?.replace(
        "@@OrderNumber@@",
        `KOT - ${
          localStorage.getItem("orderId") === null || localStorage.getItem("orderId") === undefined || localStorage.getItem("orderId") === "null"
            ? ""
            : localStorage.getItem("orderId")
        }`
      );
      printDataXml = printDataXml?.replace("@@name@@", `${selectedTableData?.name === undefined ? "" : selectedTableData?.name}`);
      printDataXml = printDataXml?.replace("@@sectionName@@", `${selectedTableData?.sectionName === undefined ? "" : selectedTableData?.sectionName}`);
      printDataXml = printDataXml?.replace("@@date@@", `${dayjs(new Date()).format("YYYY-MM-DD")}`);
      printDataXml = printDataXml?.replace("@@time@@", `${dayjs(new Date()).format("HH:mm")}`);
      let newData = "";
      Object.entries(item?.newData).map((ele, index) => {
        let uniqueRates = new Set();
        if (ele[1].length > 0 && !uniqueRates.has(index)) {
          uniqueRates.add(index);
          printDataXml = printDataXml?.replace("@@mproductcategory@@", `${ele[0]}`);

          ele[1]?.map((element) => {
            let lineData = `
      <line>
          <text align="left" length="25"> ${escapeXml(element.name.substring(0, 20))}</text>
          <text align="left" length="5">${escapeXml(element.weight)}</text>
          <text align="left" length="12">${element?.notes ? escapeXml(element.notes.substring(0, 12)) : ""}</text>
      </line>`;

            if (element.name.length > 20) {
              lineData += `
        <line>
          <text align="left" length="25">${escapeXml(element.name.substring(20))}</text>
          <text align="left" length="5"></text>
          <text align="left" length="12">${element?.notes ? escapeXml(element.notes.substring(12)) : ""}</text>
        </line>`;
            }

            if (element.name2) {
              lineData += `
        <line>
          <text align="left" length="25">${escapeXml(element.name2.substring(0, 20))}</text>
        </line>`;
              if (element.name2.length > 20) {
                lineData += `
          <line>
            <text align="left" length="25">${escapeXml(element.name2.substring(20))}</text>
          </line>`;
              }
            }

            newData += lineData;
          });
        }
      });
      printDataXml = printDataXml?.replace("@@printReciptDataItems@@", `${newData}`);
      data += printDataXml;
      mobileGroups.push({ printerId: item[0], xml: printDataXml });
    }
  });

  let finalValue = `<?xml version="1.0" encoding="UTF-8"?><output>${data}</output>`;
  const order = {
    KOTPrint: {
      print: finalValue.toString(),
      fbOrderId: fbOrderId,
    },
  };

  const onPrintFailed = (error) => {
    console.error("[kotPrinter] Failed to print KOT:", error);
    // This call had no failure handling at all before - not even a toast -
    // so a print failure here was completely invisible. Same reasoning as
    // CWKotPrinter.js: the order already reached the server, only this
    // print call failed.
    message.error(
      `KOT print failed${selectedTableData?.name ? ` for "${selectedTableData.name}"` : ""}. Please reprint manually.`,
      5
    );
  };

  if (!tillData.tillAccess.salesRep || tillData.tillAccess.salesRep === "N") {
    if (window.electronAPI?.isMobile) {
      // Desktop's hardwareController HTTP server accepts the single combined
      // `finalValue` XML and routes each group internally by its @@Printer@@
      // attribute. The mobile bridge has no such server: printTemplate() takes
      // one printerName per call, so each kitchen station's group must be sent
      // as its own call carrying that station's printerId — sending the
      // combined XML in one call (as before, with printerName hardcoded to
      // null) made every station fall back to the same default station
      // regardless of the item's real printerId.
      mobileGroups.forEach((group) => {
        const groupXml = `<?xml version="1.0" encoding="UTF-8"?><output>${group.xml}</output>`;
        window.electronAPI.hardware
          .printTemplate(group.printerId || null, kotXmlToTemplate(groupXml), { silent: true })
          .then((result) => {
            console.debug("[kotPrinter] bridge printTemplate result for printer", group.printerId, ":", result);
            if (!result?.success) throw new Error(result?.error || "print failed");
          })
          .catch(onPrintFailed);
      });
    } else {
      axios
        .post(`${printerURL}printer`, finalValue.toString(), {
          headers: {
            "Content-Type": "application/xml; charset=UTF-8",
            Accept: "application/xml",
          },
        })
        .then(() => {
        })
        .catch(onPrintFailed);
    }
  }
};
